import { ServerHintSSHRenderer } from 'infra/components/ServerCreatingProcess/ServerHintSSH/ServerHintSSH';
import { randomHelper } from 'utils/randomHelper';
import { reduxHelper } from 'utils/reduxHelper';
import { stateHelper } from 'utils/stateHelper';

import { KeyPairApi } from './KeyPairsModule.api';
import { KeyPair } from './KeyPairsModule.types';
import { savePrivateKey } from './KeyPairsModule.utils';

const CHARS_POOL =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const processKeyName = (rawName) => {
    const postfix = randomHelper.generateString(8, CHARS_POOL);
    const safeName = rawName.replace(/[^a-zA-Z0-9/-]/g, '-');
    return `${safeName}-${postfix}`;
};

type State = {
    list: KeyPair[];
    isLoaded: boolean;
    isLoading: boolean;
    hasError: boolean;
};

const keyPairsModule = stateHelper.createModuleState<State>('keyPairs', {
    list: [],
    isLoaded: false,
    isLoading: false,
    hasError: false,
});

//#region actions
const loadList = reduxHelper.createLoadListAction({
    module: keyPairsModule,
    apiMethod: KeyPairApi.loadList,
    entityName: 'keyPair',
});

const loadItem = reduxHelper.createLoadItemAction({
    module: keyPairsModule,
    apiMethod: KeyPairApi.loadKeyPair,
    entityName: 'keyPair',
    entityNamePublic: 'ключевая пара',
    idKey: 'name',
});

const removeKeyPair = reduxHelper.makeRemoveAction({
    module: keyPairsModule,
    apiMethod: KeyPairApi.removeKeyPair,
    entityName: 'keyPair',
    entityNamePublic: 'ключевая пара',
    idKey: 'name',
});

const removeKeyPairs = reduxHelper.multiplyAction(removeKeyPair);

const createKeyPair = reduxHelper.makeCreateAction<State, KeyPair>({
    module: keyPairsModule,
    apiMethod: KeyPairApi.createKeyPair,
    entityName: 'keyPair',
    idKey: 'name',
    beforeCreate: ({ keyName, namePrefix, publicKey, fromFile }) => {
        if (fromFile) {
            if (!publicKey) {
                throw new Error('Key content is empty!');
            }
            if (!keyName) {
                throw new Error('Invalid key name!');
            }
            keyName = processKeyName(keyName);
        } else if (keyName === undefined && namePrefix === undefined) {
            throw new Error('need keyName or namePrefix to create key pair');
        } else if (keyName === undefined) {
            keyName = processKeyName(namePrefix);
        }
        return { name: keyName, publicKey };
    },
    afterCreate: (keyPair) => {
        if (keyPair.privateKey) {
            savePrivateKey({
                keyName: keyPair.name,
                privateKey: keyPair.privateKey,
            });
            ServerHintSSHRenderer.render();
        }
    },
});

export const KeyPairsActions = {
    loadList,
    loadItem,
    removeKeyPairs,
    createKeyPair,
};
//#endregion

//#region selectors
const standardSelectors = reduxHelper.makeStandardSelectors<KeyPair>(
    keyPairsModule,
    { idKey: 'name' },
);

const getPrivateKey = (state, name) => {
    const keyPair = standardSelectors.getItem(state, name);
    return keyPair && keyPair.privateKey;
};

export const KeyPairsSelectors = {
    ...standardSelectors,
    getPrivateKey,
};
